import { Maybe } from 'monet';
import { TFunction } from 'i18next';
import { timestampByUTC } from '@repo-level/core-front';

import { ThirdPartySummary } from 'api/cms/thirdPartySummaries';
import { findAvailableModal } from './thirdPartySummaries';
import { JackpotLevel, JackpotWin } from 'api/common';
import { amountWithCurrencySymbol } from 'helpers/currency';

import legendLevel from 'assets/images/jackpot/levels/legends.png';
import pioneerLevel from 'assets/images/jackpot/levels/pioneers.png';
import titanLevel from 'assets/images/jackpot/levels/titans.png';

import legendLevelIcon from 'assets/images/jackpot/levels/legendsIcon.png';
import pioneerLevelIcon from 'assets/images/jackpot/levels/pioneersIcon.png';
import titanLevelIcon from 'assets/images/jackpot/levels/titansIcon.png';

export interface StaticPool {
  title: string;
  minBet: number;
  image: string;
  type: Levels;
  hitRange: string;
}

export interface Pool extends StaticPool {
  prize: string;
}

export enum Levels {
  PIONEERS = 'level_3',
  LEGENDS = 'level_2',
  TITANS = 'level_1',
}

export const mapLevels: Record<string, { title: string; icon: string }> = {
  [Levels.PIONEERS]: { title: 'shortTypes.pioneers', icon: pioneerLevelIcon },
  [Levels.LEGENDS]: { title: 'shortTypes.legends', icon: legendLevelIcon },
  [Levels.TITANS]: { title: 'shortTypes.titans', icon: titanLevelIcon },
};

export const poolsStaticContent = (t: TFunction): StaticPool[] => [
  {
    title: 'types.pioneers',
    minBet: 1,
    image: pioneerLevel,
    type: Levels.PIONEERS,
    hitRange: t('hitRangePool.pioneers'),
  },
  {
    title: 'types.legends',
    minBet: 5,
    image: legendLevel,
    type: Levels.LEGENDS,
    hitRange: t('hitRangePool.legends'),
  },
  {
    title: 'types.titans',
    minBet: 10,
    image: titanLevel,
    type: Levels.TITANS,
    hitRange: t('hitRangePool.titans'),
  },
];

export const updatePoolsWithPrizes = (
  levels: JackpotLevel[],
  currency: string,
  t: TFunction
): Pool[] =>
  poolsStaticContent(t).reduce((acc: Pool[], pool: StaticPool) => {
    const level = levels.find((lvl) => lvl.identifier === pool.type);
    if (level) {
      const prizeSum = (level.amountCents / 100).toLocaleString();

      acc.push({
        ...pool,
        prize: amountWithCurrencySymbol(currency, prizeSum),
      });
    }
    return acc;
  }, []);

export const getTotalSumWithCurrency = (
  levels: JackpotLevel[],
  currency: string
): string => {
  const totalPrizeSum = (
    levels.reduce((acc, level) => acc + level.amountCents, 0) / 100
  ).toLocaleString();

  return amountWithCurrencySymbol(currency, totalPrizeSum);
};

export const nickEncrypting = (nick: string) => {
  const stars = '***';
  const chartCount = nick.length >= 5 ? 3 : 1;

  return nick.slice(0, chartCount) + stars;
};

const formatModalId = (createdAt: string) =>
  `JACKPOT_WIN_${timestampByUTC(createdAt)}`;

export const getAvailableJackpotModal = (
  userId: number | undefined,
  thirdPartySummaries: ThirdPartySummary[] | undefined,
  jackpotWins: JackpotWin[] | undefined
) => {
  if (!userId || !jackpotWins?.length) return;

  const ids = jackpotWins.map(({ createdAt }) => formatModalId(createdAt));
  const modalId = Maybe.fromUndefined(thirdPartySummaries)
    .flatMap(findAvailableModal(ids))
    .orUndefined();

  return {
    modalId,
    modalData: modalId ? jackpotWins[ids.indexOf(modalId)] : undefined,
  };
};
